// src/pages/auth/SigninOTP.js
// for login with email and password
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/authContext";
import { useLocation, useNavigate } from "react-router-dom";
const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

export default function SigninOTP() {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  const { login } = useAuth();
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  useEffect(() => {
    updatePageTitle(
      t("auth.signInWithOTP"),
      t("auth.enterOTPToAccess")
    );
  }, [updatePageTitle, t]);

  const { state } = useLocation();
  const { email } = state || {};
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [errors, setErrors] = useState({});

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    
    // Handle paste event (detected by longer input)
    if (value.length > 1) {
      // This could be a paste operation
      const pastedValue = value.slice(0, 6); // Limit to 6 digits
      const newOtp = [...otp];
      
      // Fill the OTP array with the pasted characters
      for (let i = 0; i < pastedValue.length; i++) {
        if (index + i < 6) {
          newOtp[index + i] = pastedValue[i];
        }
      }
      
      setOtp(newOtp);
      
      // Focus on the appropriate input after paste
      const focusIndex = Math.min(index + pastedValue.length, 5);
      if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex].focus();
      }
      return;
    }
    
    // Normal single digit input
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Focus on the next input
    if (value.length === 1 && index < 5) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace
    if (e.key === "Backspace") {
      if (otp[index] === "") {
        // Current field is empty, move to previous field
        if (index > 0) {
          const newOtp = [...otp];
          newOtp[index - 1] = "";
          setOtp(newOtp);
          if (inputRefs.current[index - 1]) {
            inputRefs.current[index - 1].focus();
          }
        }
      }
    }
    
    // Handle left arrow key
    if (e.key === "ArrowLeft" && index > 0) {
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    }
    
    // Handle right arrow key
    if (e.key === "ArrowRight" && index < 5) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  // Handle paste event at container level
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text/plain").trim();
    
    // Only process if it looks like a numeric code
    if (pasteData.match(/^[0-9]+$/) && pasteData.length <= 6) {
      const newOtp = [...otp];
      
      // Fill the OTP array with the pasted characters
      for (let i = 0; i < Math.min(pasteData.length, 6); i++) {
        newOtp[i] = pasteData[i];
      }
      
      setOtp(newOtp);
      
      // Focus on the last filled input or the last input
      const focusIndex = Math.min(pasteData.length - 1, 5);
      if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex].focus();
      }
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    setErrors({});
    if (otp.some((value) => value === "")) {
      errors.otp = t("auth.otpMustBeSixDigits");
      setErrors(errors);
      return;
    }

    if (Object.keys(errors).length > 0) {
      return;
    }
    const otpString = otp.join("");

    const data = {
      email: email.trim().toLowerCase(),
      otp: otpString,
    };

    try {
      const response = await fetch(`${API_URL}/auth/signin/verify`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
      });

      const responseText = await response.text();
      console.log('Response status:', response.status);
      console.log('Response text:', responseText);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        setErrors({ general: t("auth.invalidServerResponse") });
        return;
      }

      if (!response.ok) {
        if (response.status === 404) {
          setErrors({ general: t("auth.userNotFound") });
        } else if (response.status === 400) {
          setErrors({ 
            otp: responseData.message || responseData.error || t("auth.invalidOTPCode")
          });
        } else if (response.status === 500) {
          setErrors({ general: t("auth.serverError") });
        } else {
          setErrors({ 
            general: responseData.message || responseData.error || t("auth.verificationFailed")
          });
        }
        return;
      }

      // Success case - Use auth context to handle login
      if (responseData.user && responseData.token) {
        try {
          // Use auth context login method which properly updates the authentication state
          const loginSuccess = await login(responseData.token, responseData.user);
          
          if (loginSuccess) {
            // Redirect to feed instead of profile for better UX
            navigate("/feed");
          } else {
            setErrors({ general: t("auth.failedToCompleteLogin") });
          }
        } catch (authError) {
          console.error("Auth context login error:", authError);
          setErrors({ general: t("auth.authenticationFailed") });
        }
      } else {
        setErrors({ general: t("auth.invalidResponseFormat") });
      }

    } catch (error) {
      console.error("Error during OTP verification:", error);
      setErrors({
        general: t("auth.failedToConnect"),
      });
    }
  };

  return (
    <div className="max-w-md mx-auto">
      {/* Main OTP Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 lg:p-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t("auth.verificationCode")}</h2>
          <p className="text-gray-600 mb-4">
            {t("auth.enterSixDigitCode")}
          </p>
          <p className="text-sm text-gray-500">
            {email}
          </p>
        </div>

        {/* Error Messages */}
        {(errors.general || errors.otp) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium text-red-800">
                {errors.general || errors.otp}
              </p>
            </div>
          </div>
        )}

        {/* OTP Input Form */}
        <form onSubmit={handleOtpSubmit} className="space-y-6">
          
          {/* OTP Input Grid */}
          <div className="flex justify-center space-x-3" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="1"
                value={digit}
                onChange={(e) => handleOtpChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F15213] focus:border-[#F15213] transition-colors ${
                  errors.otp ? "border-red-300 bg-red-50" : "border-gray-300"
                }`}
                autoComplete="off"
              />
            ))}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-[#F15213] hover:bg-[#F15213]/90 text-white font-semibold py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#F15213] focus:ring-offset-2"
          >
            {t("auth.verify")}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-100 text-center">
          <p className="text-sm text-gray-600 mb-3">
            {t("auth.didntReceiveCode")}
          </p>
          <button className="text-[#F15213] hover:text-[#F15213]/80 text-sm font-medium transition-colors">
            {t("auth.resendOTP")}
          </button>
        </div>
      </div>
    </div>
  );
}
